<script lang="ts">
const hours = '00,01,02,03,04,05,06,07,08,09,10,11,12,13,14,15,16,17,18,19,20,21,22,23'

import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToPicker'
});</script>
<template>
	<to-grid :key="langKey" space="1" class="to-picker-to-grid-container">
		<to-padding width="full" value="1">
			<div class="is-flex scroll-picker-grid-flex">
				<template v-if="type !== 'time'">
					<to-grid-item :side="true">
						<to-center-scroll v-model="year" :data="years" @change="yearChange"></to-center-scroll>
					</to-grid-item>
					<to-grid-item>
						<to-center-scroll v-model="month" :data="monthList" @change="monthChange"></to-center-scroll>
					</to-grid-item>
					<to-grid-item v-if="type !== 'month'">
						<to-center-scroll v-model="day" :data="days"></to-center-scroll>
					</to-grid-item>
				</template>
				<to-grid-item v-if="type === 'datetime'" width="1"></to-grid-item>
				<template v-if="type === 'datetime' || type === 'time'">
					<to-grid-item>
						<to-center-scroll v-model="hour" :data="hours"></to-center-scroll>
					</to-grid-item>
					<to-grid-item>
						<to-center-scroll v-model="minute" :data="minutes"></to-center-scroll>
					</to-grid-item>
					<to-grid-item>
						<to-center-scroll v-model="second" :data="seconds"></to-center-scroll>
					</to-grid-item>
				</template>
			</div>
		</to-padding>
	</to-grid>

	<to-gap></to-gap>
	<to-grid gap="1" col="avg">
		<to-button v-size="'l'" mode="line" color="primary" @click="$emit('cancel')">{{ t('to.datepicker.cancel') }} </to-button>
		<to-button v-size="'l'" mode="line" color="primary" @click="clear">{{ t('to.datepicker.clear') }} </to-button>
		<to-button v-size="'l'" color="primary" @click="submit">{{ t('to.datepicker.confirm') }}</to-button>
	</to-grid>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { ToButton, ToGrid, ToGridItem, ToGap, ToPadding, ToCenterScroll } from '@touchvue/components'
import { vSize } from '@touchvue/directives'
import { t, langKey } from '@touchvue/locale'



const props = defineProps({
	modelValue: {
		type: [String, Date],
		default: ''
	},
	type: {
		type: String,
		default: ''
	},
	multiple: {
		type: Boolean,
		default: false
	},
	time: {
		type: String,
		default: ''
	},
	min: {
		type: [String, Date, Number],
		default: ''
	},
	max: {
		type: [String, Date, Number],
		default: ''
	}
})

const emit = defineEmits(['cancel', 'change', 'clear'])

// 响应式数据
const inited = ref(false)
const year = ref('')
const month = ref('')
const day = ref('')
const hour = ref('')
const minute = ref('')
const second = ref('')
const years = ref([])
const months = ref('01,02,03,04,05,06,07,08,09,10,11,12')
const monthList = ref([])
const days = ref([])

const minutes = ref('00,01,02,03,04,05,06,07,08,09,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47,48,49,50,51,52,53,54,55,56,57,58,59')
const seconds = ref('00,01,02,03,04,05,06,07,08,09,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47,48,49,50,51,52,53,54,55,56,57,58,59')

// 计算属性
const valueo = computed(() => {
	const [date, time] = formatDate(props.modelValue).split(' ')
	const [year, month, day] = date.split('-')
	const [hour, minute, second] = time ? time.split(':') : ['00', '00', '00']
	return {
		year,
		month,
		day,
		hour,
		minute,
		second
	}
})

const minDate = computed(() => {
	if (props.min) {
		let min
		// 默认使用 2023-03-01 格式时，其 hour = 8 而不是 0，所以要转为 2023/03/01 格式
		if (typeof props.min === 'string') {
			min = props.min.replace(/-/gi, '/')
		} else {
			min = props.min
		}
		return new Date(min)
	} else {
		return new Date('1900-01-01')
	}
})

const maxDate = computed(() => {
	if (props.max) {
		let max
		// 默认使用 2023-03-01 格式时，其 hour = 8 而不是 0，所以要转为 2023/03/01 格式
		if (typeof props.max === 'string') {
			max = props.max.replace(/-/gi, '/')
		} else {
			max = props.max
		}
		return new Date(max)
	} else {
		return new Date('2099-12-31')
	}
})

// 监听器
watch(
	() => props.modelValue,
	() => {
		setYear(() => {
			setMonth(() => {
				setDay()
			})
		})
	}
)

// 生命周期
onMounted(() => {
	setTimeout(() => {
		if (props.type !== 'time') {
			setYear(() => {
				setMonth(() => {
					if (props.type !== 'month') {
						setDay()
					}
				})
			})
		}
		if (props.type === 'datetime' || props.type === 'time') {
			setHour()
			setMinute()
			setSecond()
		}
		inited.value = true
	}, 100)
})

// 方法
function setYear(callback) {
	function getYearsRange(currentYear, range) {
		const startYear = currentYear - range
		const endYear = currentYear + range
		const years = []

		for (let year = startYear; year <= endYear; year++) {
			years.push(String(year))
		}

		return years
	}

	// 获取当前年份
	const currentYear = new Date().getFullYear()

	// 获取前后 100 年的年份
	const yearsList = getYearsRange(currentYear, 100)

	// 设置数据
	years.value = []
	yearsList.map(item => {
		let object = {
			label: item,
			value: item
		}

		if (item >= minDate.value.getFullYear() && item <= maxDate.value.getFullYear()) {
			years.value.push(object)
		}
	})

	// 将今年设置为默认年份
	nextTick(() => {
		if (props.modelValue) {
			year.value = valueo.value.year
		} else {
			year.value = String(new Date().getFullYear())
		}
		callback && callback()
	})
}

function setMonth(callback) {
	monthList.value = []
	months.value.split(',').forEach(item => {
		let object = {
			label: item,
			value: item
		}

		const date = new Date(`${year.value}-${item}`)

		// 在 safari 或者 IOS 端，年月必须采用 '2024-07' 的String 类型
		const minDateValue = new Date(`${String(minDate.value.getFullYear())}-${addZero(String(minDate.value.getMonth() + 1))}`)
		const maxDateValue = new Date(`${String(maxDate.value.getFullYear())}-${addZero(String(maxDate.value.getMonth() + 1))}`)

		if (date >= minDateValue && date <= maxDateValue) {
			monthList.value.push(object)
		}
	})

	nextTick(() => {
		if (props.modelValue) {
			month.value = valueo.value.month
		} else if (Array.isArray(monthList.value) && monthList.value.length) {
			let monthValue
			if (props.min) {
				monthValue = monthList.value[0].value
			} else {
				monthValue = new Date().getMonth() + 1
			}
			const date = new Date(`${String(year.value)}-${addZero(String(monthValue))}`)
			month.value = addZero(String(date.getMonth() + 1))
		} else {
			month.value = addZero(String(new Date().getMonth() + 1))
		}
		callback && callback()
	})
}

function setDay() {
	const total = new Date(Number(year.value), Number(month.value), 0).getDate()
	days.value = []

	for (let i = 0; i < total; i++) {
		const dayValue = addZero(i + 1)

		let object = {
			label: dayValue,
			value: dayValue,
			disabled: false
		}

		// 在年月都在可用范围时，再判断天
		const minDateValue = new Date(minDate.value.getFullYear(), minDate.value.getMonth(), minDate.value.getDate())
		const maxDateValue = new Date(maxDate.value.getFullYear(), maxDate.value.getMonth(), maxDate.value.getDate())
		const date = new Date(Number(year.value), Number(month.value) - 1, Number(dayValue))
		// 检查日期是否在min/max范围内
		const inDateRange = date >= minDateValue && date <= maxDateValue

		if (inDateRange) {
			days.value.push(object)
		}
	}

	nextTick(() => {
		if (props.modelValue) {
			day.value = valueo.value.day
		} else if (Array.isArray(days.value) && days.value.length) {
			const dayValue = new Date().getDate()
			const date = new Date(`${year.value}/${month.value}/${dayValue}`)
			day.value = addZero(String(date.getDate()))
		} else {
			day.value = addZero(String(new Date().getDate()))
		}
	})
}

function setHour() {
	if (props.modelValue) {
		hour.value = valueo.value.hour
	} else {
		hour.value = addZero(String(new Date().getHours()))
	}
}

function setMinute() {
	if (props.modelValue) {
		minute.value = valueo.value.minute
	} else {
		minute.value = addZero(String(new Date().getMinutes()))
	}
}

function setSecond() {
	if (props.modelValue) {
		second.value = valueo.value.second
	} else {
		second.value = addZero(String(new Date().getSeconds()))
	}
}

function yearChange() {
	if (inited.value) {
		setMonth(() => {
			setDay()
		})
	}
}

function monthChange() {
	if (inited.value) {
		setDay()
	}
}

function submit() {
	if (props.type === 'datetime') {
		emit('change', `${year.value}-${month.value}-${day.value} ${hour.value}:${minute.value}:${second.value}`)
	} else if (props.type === 'time') {
		emit('change', `${hour.value}:${minute.value}:${second.value}`)
	} else {
		emit('change', `${year.value}-${month.value}-${day.value}`)
	}
}

function clear() {
	emit('clear')
}

// 格式化日期
function formatDate(date: string | Date, fmt?: string): string {
	let dateObj: Date
	if (typeof date === 'string') {
		dateObj = new Date(date)
	} else {
		dateObj = date
	}

	if (!fmt) {
		fmt = 'yyyy-MM-dd HH:mm:ss'
	}
	if (/(y+)/.test(fmt)) {
		fmt = fmt.replace(RegExp.$1, (dateObj.getFullYear() + '').substr(4 - RegExp.$1.length))
	}
	const o: { [key: string]: number } = {
		'M+': dateObj.getMonth() + 1,
		'd+': dateObj.getDate(),
		'H+': dateObj.getHours(),
		'm+': dateObj.getMinutes(),
		's+': dateObj.getSeconds()
	}
	for (const k in o) {
		if (new RegExp('(' + k + ')').test(fmt)) {
			const str = o[k] + ''
			fmt = fmt.replace(RegExp.$1, RegExp.$1.length === 1 ? str : addZero(str))
		}
	}
	return fmt
}

function addZero(str: string | number): string {
	return ('00' + str).substr(str.toString().length)
}
</script>
